import React, { useCallback, useMemo, useState } from "react";
import { useLocation } from "wouter";
import {
  Box,
  Typography,
  Link,
  Divider,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Visibility, VisibilityOff, Google } from "@mui/icons-material";

import logo from "../../assets/nwt.png";
import { useRegisterMutation, useSsoLoginMutation } from "../../services";
import { notify } from "../../components/Notification";
import { DateInputField } from "../../components/Input/DateInput/DateInputField";
import { isAdult, MIN_AGE } from "../../utils/age";
import { toIsoDate } from "../../utils/dates";
import {
  PasswordStrength,
  RegisterFormData,
  RegisterProps,
  RegisterUserType,
} from "./types";
import { ANIMATION } from "../../utils/constants";
import {
  BrandIcon,
  BrandSection,
  GoogleButton,
  Card,
  LoginContainer,
  StyledTextField,
} from "./styles";
import Button, { ButtonTypes } from "../../components/Button";
import { IconButton } from "../../components/IconButton";

function getBgColor(color: string): string {
  if (color === "success") {
    return "success.main";
  } else if (color === "warning") {
    return "warning.main";
  } else {
    return "error.main";
  }
}

const PASSWORD_MIN_LENGTH = 8;

export const RegisterPage: React.FC<RegisterProps> = ({ getUser }) => {
  const [, navigate] = useLocation();
  const [formData, setFormData] = useState<RegisterFormData>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    dateOfBirth: null,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptNewsletter, setAcceptNewsletter] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [register] = useRegisterMutation();
  const [ssoLogin] = useSsoLoginMutation();

  const passwordStrength = useMemo((): PasswordStrength => {
    const password = formData.password;
    if (!password) {
      return { score: 0, feedback: [], color: "error" };
    }

    const checks = {
      length: password.length >= PASSWORD_MIN_LENGTH,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    };

    const score = Object.values(checks).filter(Boolean).length;
    const feedback: string[] = [];

    if (!checks.length) feedback.push("At least 8 characters");
    if (!checks.lowercase) feedback.push("One lowercase letter");
    if (!checks.uppercase) feedback.push("One uppercase letter");
    if (!checks.number) feedback.push("One number");
    if (!checks.special) feedback.push("One special character");

    let color: "error" | "warning" | "success" = "error";
    if (score >= 4) color = "success";
    else if (score >= 2) color = "warning";

    return { score, feedback, color };
  }, [formData.password]);

  const handleInputChange = useCallback(
    (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFormData((prev) => ({ ...prev, [field]: value }));

      // Clear field-specific error
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    },
    [errors],
  );

  const handleDateOfBirthChange = useCallback(
    (value: Date | null) => {
      setFormData((prev) => ({ ...prev, dateOfBirth: value }));
      if (errors.dateOfBirth) {
        setErrors((prev) => ({ ...prev, dateOfBirth: "" }));
      }
    },
    [errors.dateOfBirth],
  );

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    // Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    // Email validation
    if (!formData.email) {
      newErrors.email = "Email address is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (passwordStrength.score < 3) {
      newErrors.password = "Password is too weak";
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    // Date of birth validation
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = "Date of birth is required";
    } else if (!isAdult(formData.dateOfBirth)) {
      newErrors.dateOfBirth = `You must be at least ${MIN_AGE} years old to register`;
    }

    // Terms acceptance
    if (!acceptTerms) {
      newErrors.terms = "You must accept the Terms of Service";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, passwordStrength.score, acceptTerms]);

  // Form submission handler
  const handleRegister = useCallback(
    async (e: React.SubmitEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      const { dateOfBirth } = formData;
      if (!dateOfBirth) {
        return;
      }

      setLoading(true);

      try {
        const registerUser: RegisterUserType = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          password: formData.password,
          confirmPassword: formData.confirmPassword,
          dateOfBirth: toIsoDate(dateOfBirth),
          newsletter: acceptNewsletter,
        };
        await register(registerUser).unwrap();

        notify.success(
          "Account created successfully! Please check your email to verify your account.",
        );

        getUser();
        navigate("/");
      } catch {
        // errorToastMiddleware raises the toast; keep the form filled in.
      } finally {
        setLoading(false);
      }
    },
    [validateForm, formData, acceptNewsletter, register, getUser, navigate],
  );

  // Google registration handler
  const handleGoogleRegister = useCallback(
    async (event: React.MouseEvent) => {
      event.preventDefault();
      setLoading(true);

      try {
        const url = await ssoLogin().unwrap();
        globalThis.location.href = url;
      } catch {
        // errorToastMiddleware raises the toast.
      } finally {
        setLoading(false);
      }
    },
    [ssoLogin],
  );

  const passwordStrengthText = useMemo(() => {
    if (passwordStrength.score === 0) return "";
    if (passwordStrength.score < 2) return "Weak";
    if (passwordStrength.score < 4) return "Fair";
    return "Strong";
  }, [passwordStrength.score]);

  return (
    <LoginContainer>
      <Card elevation={0}>
        <BrandSection>
          <BrandIcon>
            <img src={logo} alt="Net Worth Tracker" />
          </BrandIcon>
          <Typography variant="h4" sx={{ fontWeight: "bold" }} gutterBottom>
            Create Account
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Start tracking your complete financial picture
          </Typography>
        </BrandSection>

        <form onSubmit={handleRegister}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <StyledTextField
              fullWidth
              label="First Name"
              variant="outlined"
              value={formData.firstName}
              onChange={handleInputChange("firstName")}
              disabled={loading}
              error={!!errors.firstName}
              helperText={errors.firstName}
              autoComplete="given-name"
              autoFocus
            />
            <StyledTextField
              fullWidth
              label="Last Name"
              variant="outlined"
              value={formData.lastName}
              onChange={handleInputChange("lastName")}
              disabled={loading}
              error={!!errors.lastName}
              helperText={errors.lastName}
              autoComplete="family-name"
            />
          </Box>

          <StyledTextField
            fullWidth
            label="Email Address"
            type="email"
            variant="outlined"
            value={formData.email}
            onChange={handleInputChange("email")}
            disabled={loading}
            error={!!errors.email}
            helperText={errors.email}
            autoComplete="email"
          />

          <StyledTextField
            fullWidth
            label="Password"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            value={formData.password}
            onChange={handleInputChange("password")}
            disabled={loading}
            error={!!errors.password}
            helperText={
              errors.password ||
              (formData.password && passwordStrength.feedback.length > 0
                ? `Missing: ${passwordStrength.feedback.join(", ")}`
                : "")
            }
            autoComplete="new-password"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          {formData.password && (
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Password Strength:
                </Typography>
                <Typography
                  variant="caption"
                  color={passwordStrength.color}
                  sx={{ fontWeight: "medium" }}
                >
                  {passwordStrengthText}
                </Typography>
              </Box>
              <Box
                sx={{
                  height: 4,
                  bgcolor: "var(--color-border)",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: "100%",
                    transformOrigin: "left",
                    transform: `scaleX(${passwordStrength.score / 5})`,
                    bgcolor: getBgColor(passwordStrength.color),
                    transition: `transform ${ANIMATION.duration.moderate} ${ANIMATION.easing.easeOut}`,
                  }}
                />
              </Box>
            </Box>
          )}

          <StyledTextField
            fullWidth
            label="Confirm Password"
            type={showConfirmPassword ? "text" : "password"}
            variant="outlined"
            value={formData.confirmPassword}
            onChange={handleInputChange("confirmPassword")}
            disabled={loading}
            error={!!errors.confirmPassword}
            helperText={errors.confirmPassword}
            autoComplete="new-password"
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      disabled={loading}
                      aria-label={
                        showConfirmPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          <Box sx={{ mb: 2 }}>
            <DateInputField
              testId="register-dob"
              label="Date of Birth"
              value={formData.dateOfBirth}
              onChange={handleDateOfBirthChange}
              placeholder="YYYY/MM/DD"
              disabled={loading}
              error={errors.dateOfBirth}
              maxDate={new Date()}
              autoComplete="bday"
            />
          </Box>

          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptTerms}
                  onChange={(e) => {
                    setAcceptTerms(e.target.checked);
                    if (errors.terms) {
                      setErrors((prev) => ({ ...prev, terms: "" }));
                    }
                  }}
                  disabled={loading}
                  sx={{
                    "&.Mui-checked": { color: "var(--color-primary)" },
                  }}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  I agree to the{" "}
                  <Link
                    href="/terms"
                    sx={{ color: "var(--color-primary)" }}
                    underline="hover"
                  >
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link
                    href="/privacy"
                    sx={{ color: "var(--color-primary)" }}
                    underline="hover"
                  >
                    Privacy Policy
                  </Link>
                </Typography>
              }
            />
            {errors.terms && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: "block", mt: 0.5 }}
              >
                {errors.terms}
              </Typography>
            )}
          </Box>

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={acceptNewsletter}
                  onChange={(e) => setAcceptNewsletter(e.target.checked)}
                  disabled={loading}
                  sx={{
                    "&.Mui-checked": { color: "var(--color-primary)" },
                  }}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  Send me financial tips and product updates (optional)
                </Typography>
              }
            />
          </Box>

          <Button
            buttonType="submit"
            type={ButtonTypes.primary}
            isFullWidth={true}
            size="large"
            isLoading={loading}
          >
            {loading ? "Creating Account..." : "Create Account"}
          </Button>
        </form>

        <Divider sx={{ my: 3 }}>
          <Typography variant="body2" color="text.secondary">
            OR
          </Typography>
        </Divider>

        <GoogleButton
          type={ButtonTypes.cancel}
          size="large"
          isFullWidth
          onClick={handleGoogleRegister}
          disabled={loading}
        >
          <Google />
          Sign up with Google
        </GoogleButton>

        <Box sx={{ textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            Already have an account?{" "}
            <Link
              href="/login"
              sx={{ color: "var(--color-primary)", fontWeight: "medium" }}
              underline="hover"
            >
              Sign in here
            </Link>
          </Typography>
        </Box>
      </Card>
    </LoginContainer>
  );
};
